import { Box, Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import { ICON_REPLACED, IMAGE_REPLACED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { EDITOR_REPLACE_MENU_TABLIST_ID } from 'modules/Editor/Editor.config';
import { useBoundingBox } from 'modules/Editor/components/BoundingBox/useBoundingBox';
import { useFocus } from 'modules/Editor/components/Focus/useFocus';
import {
  RootState,
  addNewWidgetAndReplaceExisting,
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from 'modules/Editor/store';
import { selectReplaceWidgetMenuIndex } from 'modules/Editor/store/editorSettingsSelector';
import { setActiveWidgetMenu, setReplaceWidgetMenuIndex } from 'modules/Editor/store/editorSettingsSlice';
import { ReplaceMenuTabIndex } from 'modules/Editor/store/editorSettingsSlice.types';
import { selectWidgetProperty } from 'modules/Editor/store/infographSelector';
import { updateWidget } from 'modules/Editor/store/infographSlice';
import { selectActiveWidgets } from 'modules/Editor/store/widgetSelector';
import { useUser } from 'modules/User';
import { ReactElement, memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { WIDGET_MENU_OPTIONS } from 'types/WidgetMenu.types';
import { IconColorOption } from 'types/icon.types';
import { WidgetType } from 'types/widget.types';
import { IconWidgetData, IconWidgetType } from 'widgets/IconWidget/IconWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { NewWidget } from 'widgets/Widget.types';

import { BrandKitMenu } from '../BrandKitMenu';
import { IconWidgetMenu } from '../IconWidgetMenu';
import { UploadMenu } from '../ImageUploadMenu/UploadMenu';
import { PhotoWidgetMenu } from '../PhotoWidgetMenu';
import {
  replaceDataDifferentWidgetType,
  replaceDataSameWidgetType,
  shouldIncludePhotosTab,
  widgetHasReplace,
} from './ReplaceMenu.helper';

type ReplaceOptions = {
  [label: string]: ReactElement;
};

export const ReplaceMenu = memo(() => {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const { boundingBox } = useBoundingBox();
  const { setWidgetFocus } = useFocus();

  const { isEducationUser } = useUser();

  // menu properties
  const tabIndex = useAppSelector(selectReplaceWidgetMenuIndex);

  // widget properties
  const activeWidgets = useAppSelector(selectActiveWidgets) || {};
  const hasOneActiveWidget = activeWidgets?.length === 1;
  const widgetId = activeWidgets[0]?.id;
  const widgetType = widgetId && getWidgetTypeFromId(widgetId);
  const type = useAppSelector(selectWidgetProperty<IconWidgetData, 'type'>(widgetId, 'type'));

  const setTabIndex = useCallback((index: number) => dispatch(setReplaceWidgetMenuIndex(index)), [dispatch]);

  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.replaceMenu' });

  const handleReplaceWidget = (newWidget: NewWidget | NewWidget[], viewBox: string = '') => {
    const { widgetData: newWidgetData, widgetType: newWidgetType } = newWidget as NewWidget;
    const widgetType = getWidgetTypeFromId(widgetId);

    // access the widget data when the function is called
    // we don't need to subscribe to the data - we just need it when this handleReplaceWidget is called
    const widgetData = (store.getState() as RootState).infograph.widgets[widgetId];

    if (widgetType === newWidgetType) {
      const replaceData = replaceDataSameWidgetType({ widgetId, newWidgetData, widgetData, viewBox });
      dispatch(updateWidget({ widgetId, widgetData: replaceData }));
      setWidgetFocus(widgetId);
    } else if (!Array.isArray(newWidget)) {
      const newWidgetData = replaceDataDifferentWidgetType(newWidget, widgetData);
      dispatch(addNewWidgetAndReplaceExisting({ widgetId, newWidgetData }));
    }

    // MP tracking, don't distinguish between image from either image search or upload
    if (widgetType === WidgetType.Icon) {
      const replacedByType =
        newWidgetType === WidgetType.Icon
          ? ICON_REPLACED.category.ReplacedByIcon
          : ICON_REPLACED.category.ReplacedByImage;
      Mixpanel.track(ICON_REPLACED.eventName, { category: replacedByType });
    } else if (widgetType === WidgetType.Image) {
      const replacedByType =
        newWidgetType === WidgetType.Icon
          ? IMAGE_REPLACED.category.ReplacedByIcon
          : IMAGE_REPLACED.category.ReplacedByImage;
      Mixpanel.track(IMAGE_REPLACED.eventName, { category: replacedByType });
    }

    setTimeout(() => boundingBox.updateRect(), 0);
  };

  const replaceOptions: ReplaceOptions = {
    Icons: (
      <IconWidgetMenu
        colorOptions={type === IconWidgetType.Grid ? [IconColorOption.Mono] : undefined}
        isIconWidgetMenuActive
        onClickIconWidget={handleReplaceWidget}
      />
    ),
  };

  if (shouldIncludePhotosTab(widgetType, type)) {
    replaceOptions.Photos = <PhotoWidgetMenu isMenuActive onItemClick={handleReplaceWidget} />;
    replaceOptions.Uploads = <UploadMenu onAddUploadImage={handleReplaceWidget} />;

    if (!isEducationUser) {
      replaceOptions.Logos = <BrandKitMenu onItemClick={handleReplaceWidget} />;
    }
  }

  const replaceTabs = Object.keys(replaceOptions);

  // When the replace menu is opened or when the widget is changed while the menu is open
  useEffect(() => {
    // multi-select or no active widget - close the menu
    if (!widgetId || !hasOneActiveWidget) {
      dispatch(setActiveWidgetMenu(WIDGET_MENU_OPTIONS.NONE));
      return;
    }

    // close the menu if the widget doesn't have a the replace option
    const widgetType = getWidgetTypeFromId(widgetId);
    if (!widgetHasReplace(widgetId)) {
      dispatch(setActiveWidgetMenu(WIDGET_MENU_OPTIONS.NONE));
      return;
    }

    // if the widget doesn't have the photos tab, set the tab index to the first tab
    const state = store.getState() as RootState;
    const type = selectWidgetProperty<IconWidgetData, 'type'>(widgetId, 'type')(state);
    const tabIndex = selectReplaceWidgetMenuIndex(state);
    if (!shouldIncludePhotosTab(widgetType, type) && tabIndex !== ReplaceMenuTabIndex.ICON) {
      dispatch(setReplaceWidgetMenuIndex(ReplaceMenuTabIndex.ICON));
    }
  }, [widgetId, hasOneActiveWidget, dispatch, store]);

  return (
    <Box h='full' data-testid='replace-menu'>
      <Tabs variant='left-menu' index={tabIndex} onChange={setTabIndex} overflow='hidden' isLazy>
        <TabList id={EDITOR_REPLACE_MENU_TABLIST_ID} h={10}>
          {replaceTabs.map((tab) => (
            <Tab key={tab}>{t(tab.toLowerCase() as keyof typeof t)}</Tab>
          ))}
        </TabList>
        <TabPanels overflow='auto'>
          {replaceTabs.map((tab) => (
            <TabPanel key={tab} data-testid={`${tab}-tab-panel`}>
              {replaceOptions[tab]}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
});
ReplaceMenu.displayName = 'ReplaceMenu';
